import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG';
import { Link } from 'react-router-dom';

// ============================================================
// 1. SÉCURISATION DES URLs (HTTPS)
// ============================================================
const API_BASE = API_CONFIG2.apiUrl;
const secureApiBase = window.location.protocol === 'https:'
  ? API_BASE.replace(/^http:/, 'https:')
  : API_BASE;

const API_URL = `${secureApiBase}formation/api_formation.php?action=fetch&limit=1000`;

// ============================================================
// 2. CACHE DEXIE (2 heures)
// ============================================================
const db = new Dexie('FormationsCache');
db.version(1).stores({
  formations: 'id, titre, description, categorie, duree, participantsLimit, image, timestamp'
});

const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 heures

// ============================================================
// 3. COMPOSANT PARTICULES (fond animé)
// ============================================================
const ParticlesBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2.5 + 1;
        this.speedX = (Math.random() - 0.5) * 0.6;
        this.speedY = (Math.random() - 0.5) * 0.6;
        this.opacity = Math.random() * 0.6 + 0.2;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180, 210, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
      for (let i = 0; i < count; i++) {
        particles.push(new Particle());
      }
    };
    initParticles();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      // Lignes entre particules proches
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(180, 210, 255, ${0.08 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.7,
      }}
    />
  );
};

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
const FormationsCards = () => {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);

  // Palette de couleurs pour les catégories
  const getCategoryColor = (categorie) => {
    const colors = {
      'Management': '#4f8cff',
      'IT': '#6bb3e8',
      'Languages': '#5fc3e4',
      'Soft Skills': '#4a9eff',
      'Finance': '#3d7ed9',
      'Marketing': '#5b8def',
      'RH': '#e67e22',
      'Leadership': '#a569bd',
    };
    return colors[categorie] || '#4a90e2';
  };

  // Construire l'URL complète de l'image (avec sécurisation HTTPS)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      if (window.location.protocol === 'https:' && imagePath.startsWith('http://')) {
        return imagePath.replace('http://', 'https://');
      }
      return imagePath;
    }
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${secureApiBase}${cleanPath}`;
  };

  // ============================================================
  // 5. RÉCUPÉRATION DES FORMATIONS (avec cache)
  // ============================================================
  const fetchFormations = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      if (!forceRefresh) {
        const cached = await db.formations.toArray();
        if (cached.length > 0) {
          const cacheEntry = cached[0];
          const now = Date.now();
          if (now - cacheEntry.timestamp < CACHE_DURATION) {
            setFormations(JSON.parse(cacheEntry.data));
            setLoading(false);
            return;
          } else {
            await db.formations.clear();
          }
        }
      }

      let attempts = 0;
      let success = false;
      let response;

      while (attempts < 3 && !success) {
        attempts++;
        try {
          response = await fetch(API_URL);
          if (response.status === 429) {
            const delay = 5000 * attempts;
            setError(`Trop de requêtes, nouvelle tentative dans ${delay/1000}s...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          if (!response.ok) {
            throw new Error(`Erreur HTTP ${response.status}`);
          }
          const json = await response.json();
          if (json.status === 'success' && Array.isArray(json.data)) {
            const formationsData = json.data;
            await db.formations.clear();
            await db.formations.add({
              id: 1,
              data: JSON.stringify(formationsData),
              timestamp: Date.now()
            });
            setFormations(formationsData);
            success = true;
            setError(null);
          } else {
            throw new Error(json.message || 'Format de réponse invalide');
          }
        } catch (err) {
          if (attempts >= 3) throw err;
        }
      }
      if (!success) {
        throw new Error('Échec après 3 tentatives');
      }
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
      const cached = await db.formations.toArray();
      if (cached.length > 0) {
        const cacheEntry = cached[0];
        setFormations(JSON.parse(cacheEntry.data));
        setError('Données obsolètes (cache) - ' + (error || ''));
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [error]);

  // ============================================================
  // 6. EFFETS
  // ============================================================
  useEffect(() => {
    fetchFormations();
  }, [fetchFormations]);

  // ============================================================
  // 7. GESTION DES ACTIONS
  // ============================================================
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchFormations(true);
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      await db.formations.clear();
      await fetchFormations(true);
    } catch (err) {
      setError('Erreur lors du vidage du cache');
    } finally {
      setClearing(false);
    }
  };

  const openModal = (formation) => setSelectedFormation(formation);
  const closeModal = () => setSelectedFormation(null);

  // ============================================================
  // 8. RENDU
  // ============================================================
  return (
    <>
      {/* Fond de particules */}
      <ParticlesBackground />

      <style>{`
        /* ============================================
           STYLES GLOBAUX & FOND
           ============================================ */
        .formations-page {
          position: relative;
          z-index: 1;
          padding: 80px 24px 60px;
          min-height: 100vh;
          max-width: 100%;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #f0f4ff;
          background: radial-gradient(ellipse at 30% 10%, rgba(30, 60, 120, 0.3) 0%, transparent 60%),
                      radial-gradient(ellipse at 70% 90%, rgba(20, 40, 90, 0.2) 0%, transparent 60%),
                      #0b1428;
          backdrop-filter: blur(2px);
        }

        .formations-container {
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        /* ============================================
           SUPER TITRE
           ============================================ */
        .formations-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 48px;
          padding: 0 10px;
        }

        .title-wrapper {
          display: flex;
          flex-direction: column;
        }

        .title-wrapper .main-title {
          font-size: clamp(2.6rem, 6vw, 4.2rem);
          font-weight: 900;
          margin: 0;
          line-height: 1.1;
          letter-spacing: -1px;
          background: linear-gradient(135deg, #6bb3e8 0%, #b0d4ff 40%, #6bb3e8 80%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 5s ease-in-out infinite alternate;
          text-shadow: 0 0 40px rgba(107, 179, 232, 0.15);
          position: relative;
          display: inline-block;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }

        .title-wrapper .sub-title {
          font-size: clamp(0.9rem, 1.2vw, 1.2rem);
          color: rgba(255, 255, 255, 0.5);
          font-weight: 400;
          margin-top: 4px;
          letter-spacing: 2px;
          text-transform: uppercase;
          opacity: 0.8;
        }

        /* Boutons d'action */
        .actions-group {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-action {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 28px;
          backdrop-filter: blur(8px);
          color: #ffffff;
          font-weight: 600;
          font-size: 0.9rem;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.10);
          background: rgba(255, 255, 255, 0.06);
          user-select: none;
        }

        .btn-action:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
          border-color: rgba(255, 255, 255, 0.25);
        }

        .btn-action:active:not(:disabled) {
          transform: scale(0.96);
        }

        .btn-action:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          transform: none !important;
        }

        .btn-action i {
          font-size: 1rem;
          transition: transform 0.6s ease;
        }

        .btn-action.refresh:not(:disabled):hover i {
          transform: rotate(360deg);
        }

        .btn-action.clear {
          border-color: rgba(239, 68, 68, 0.3);
          color: #f87171;
          background: rgba(239, 68, 68, 0.06);
        }

        .btn-action.clear:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.15);
          border-color: #ef4444;
          box-shadow: 0 8px 30px rgba(239, 68, 68, 0.15);
        }

        /* ============================================
           ÉTATS (chargement, erreur, vide)
           ============================================ */
        .state-wrapper {
          text-align: center;
          padding: 80px 20px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border-radius: 32px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
        }

        .state-wrapper .spinner {
          display: inline-block;
          width: 60px;
          height: 60px;
          border: 3px solid rgba(255, 255, 255, 0.05);
          border-top: 3px solid #6bb3e8;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
          box-shadow: 0 0 20px rgba(107, 179, 232, 0.2);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .state-wrapper p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1.1rem;
          margin: 0;
        }

        .state-wrapper .error-icon {
          font-size: 3.5rem;
          margin-bottom: 12px;
          display: block;
        }

        .state-wrapper .error-text {
          color: #ffb3b3;
        }

        /* ============================================
           GRILLE DE CARTES
           ============================================ */
        .formations-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }

        /* ============================================
           CARTES – GLASSMORPHISM AVEC ANIMATIONS
           ============================================ */
        .formation-card {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
                      box-shadow 0.5s ease,
                      border-color 0.4s ease,
                      background 0.4s ease;
          opacity: 0;
          transform: translateY(50px) scale(0.96);
          animation: cardFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          will-change: transform;
        }

        .formation-card:nth-child(1) { animation-delay: 0.05s; }
        .formation-card:nth-child(2) { animation-delay: 0.10s; }
        .formation-card:nth-child(3) { animation-delay: 0.15s; }
        .formation-card:nth-child(4) { animation-delay: 0.20s; }
        .formation-card:nth-child(5) { animation-delay: 0.25s; }
        .formation-card:nth-child(6) { animation-delay: 0.30s; }
        .formation-card:nth-child(7) { animation-delay: 0.35s; }
        .formation-card:nth-child(8) { animation-delay: 0.40s; }
        .formation-card:nth-child(9) { animation-delay: 0.45s; }
        .formation-card:nth-child(10) { animation-delay: 0.50s; }
        .formation-card:nth-child(11) { animation-delay: 0.55s; }
        .formation-card:nth-child(12) { animation-delay: 0.60s; }

        @keyframes cardFadeUp {
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .formation-card:hover {
          transform: translateY(-12px) scale(1.02);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
          border-color: rgba(255, 255, 255, 0.20);
          background: rgba(255, 255, 255, 0.08);
        }

        /* Image */
        .formation-card-image {
          width: 100%;
          height: 200px;
          background: rgba(255, 255, 255, 0.02);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
          position: relative;
        }

        .formation-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .formation-card:hover .formation-card-image img {
          transform: scale(1.08);
        }

        .formation-card-image .no-image {
          color: rgba(255, 255, 255, 0.10);
          font-size: 3.5rem;
        }

        /* En-tête de carte */
        .formation-card-header {
          padding: 20px 22px 14px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

        .formation-card-header .card-title {
          flex: 1;
          min-width: 0;
        }

        .formation-card-header .card-title h3 {
          font-size: 1.15rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 4px;
          line-height: 1.3;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          word-break: break-word;
        }

        .formation-card-header .card-title .card-id {
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.25);
          font-weight: 500;
          letter-spacing: 0.3px;
        }

        .formation-card-header .card-badge {
          display: inline-flex;
          padding: 5px 16px;
          border-radius: 30px;
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          white-space: nowrap;
          flex-shrink: 0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        /* Corps */
        .formation-card-body {
          padding: 16px 22px 22px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .formation-card-body .card-description {
          font-size: 0.9rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.65);
          margin: 0 0 18px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }

        .formation-card-body .card-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 18px;
        }

        .formation-card-body .card-meta-item {
          display: flex;
          flex-direction: column;
          background: rgba(255, 255, 255, 0.03);
          padding: 10px 12px;
          border-radius: 14px;
          border: 1px solid rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(4px);
          transition: background 0.3s;
        }

        .formation-card:hover .card-meta-item {
          background: rgba(255, 255, 255, 0.06);
        }

        .formation-card-body .card-meta-item .meta-label {
          font-size: 0.6rem;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.35);
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .formation-card-body .card-meta-item .meta-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: #ffffff;
          margin-top: 2px;
        }

        .formation-card-body .card-footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          padding-top: 16px;
          margin-top: auto;
        }

        .formation-card-body .card-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #7bb8f0;
          font-weight: 600;
          font-size: 0.9rem;
          text-decoration: none;
          transition: gap 0.3s ease, color 0.3s ease;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px 0;
        }

        .formation-card-body .card-link:hover {
          gap: 16px;
          color: #b0d4ff;
        }

        .formation-card-body .card-link i {
          font-size: 0.8rem;
          transition: transform 0.3s ease;
        }

        .formation-card-body .card-link:hover i {
          transform: translateX(6px);
        }

        /* ============================================
           MODAL
           ============================================ */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(14px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: modalFadeIn 0.3s ease;
        }

        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: rgba(20, 35, 70, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.10);
          border-radius: 32px;
          max-width: 640px;
          width: 100%;
          padding: 40px 36px 36px;
          box-shadow: 0 40px 90px rgba(0, 0, 0, 0.6);
          animation: modalSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        @keyframes modalSlideUp {
          from { transform: translateY(50px) scale(0.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }

        .modal-close {
          position: absolute;
          top: 16px;
          right: 20px;
          background: rgba(255, 255, 255, 0.04);
          border: none;
          color: rgba(255, 255, 255, 0.4);
          font-size: 1.6rem;
          cursor: pointer;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s, color 0.3s, transform 0.3s;
        }

        .modal-close:hover {
          background: rgba(255, 255, 255, 0.10);
          color: #ffffff;
          transform: rotate(90deg);
        }

        .modal-header {
          margin-bottom: 24px;
          padding-right: 40px;
        }

        .modal-header .modal-badge {
          display: inline-block;
          padding: 6px 18px;
          border-radius: 50px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          color: #ffffff;
          margin-bottom: 12px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .modal-header h3 {
          font-size: 2rem;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 4px;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        }

        .modal-header .modal-id {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.3);
        }

        .modal-image {
          width: 100%;
          max-height: 260px;
          object-fit: cover;
          border-radius: 18px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          background: rgba(255, 255, 255, 0.02);
        }

        .modal-body {
          color: rgba(255, 255, 255, 0.85);
        }

        .modal-body .modal-description {
          font-size: 1rem;
          line-height: 1.7;
          margin: 0 0 24px;
        }

        .modal-body .modal-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 24px;
        }

        .modal-body .modal-detail-item {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 16px;
          padding: 14px 18px;
          border: 1px solid rgba(255, 255, 255, 0.04);
        }

        .modal-body .modal-detail-item .detail-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.35);
          font-weight: 600;
          letter-spacing: 0.4px;
        }

        .modal-body .modal-detail-item .detail-value {
          font-size: 1rem;
          font-weight: 600;
          color: #ffffff;
          margin-top: 4px;
        }

        /* ============================================
           RESPONSIVE
           ============================================ */
        @media (max-width: 1200px) {
          .formations-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }
        }

        @media (max-width: 992px) {
          .formations-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .formations-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .title-wrapper .main-title {
            text-align: center;
          }
        }

        @media (max-width: 576px) {
          .formations-grid {
            grid-template-columns: 1fr;
            max-width: 420px;
            margin: 0 auto;
          }
          .formations-page {
            padding: 60px 16px 40px;
          }
          .modal-content {
            padding: 28px 20px;
          }
          .modal-header h3 {
            font-size: 1.6rem;
          }
          .modal-body .modal-details {
            grid-template-columns: 1fr;
          }
          .formation-card-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .formation-card-header .card-badge {
            align-self: flex-start;
          }
          .formation-card-body .card-meta {
            grid-template-columns: 1fr 1fr;
          }
          .actions-group {
            width: 100%;
            justify-content: center;
          }
          .btn-action {
            flex: 1;
            justify-content: center;
            padding: 12px 16px;
            font-size: 0.85rem;
          }
        }

        /* Fallback FontAwesome (si non chargé) */
        .fa, .fas, .far, .fal, .fab {
          font-family: 'Font Awesome 6 Free' !important;
          font-weight: 900;
        }
        .fa-sync::before { content: "↻"; font-style: normal; }
        .fa-arrow-right::before { content: "→"; font-style: normal; }
        .fa-times::before { content: "✕"; font-style: normal; }
        .fa-image::before { content: "🖼️"; font-style: normal; }
        .fa-eraser::before { content: "🧹"; font-style: normal; }
      `}</style>

      <section className="formations-page">
        <div className="formations-container">
          <div className="formations-header">
            <div className="title-wrapper">
              <h1 className="main-title">✨ Nos formations</h1>
              <span className="sub-title">Développez vos compétences avec excellence</span>
            </div>
            <div className="actions-group">
              <button
                className="btn-action refresh"
                onClick={handleRefresh}
                disabled={loading || isRefreshing || clearing}
              >
                <i className="fa fa-sync"></i>
                {isRefreshing ? 'Rafraîchissement...' : 'Rafraîchir'}
              </button>
              <button
                className="btn-action clear"
                onClick={handleClearCache}
                disabled={loading || isRefreshing || clearing}
              >
                <i className="fa fa-eraser"></i>
                {clearing ? 'Vidage...' : 'Vider le cache'}
              </button>
            </div>
          </div>

          {loading && (
            <div className="state-wrapper">
              <div className="spinner"></div>
              <p>Chargement des formations...</p>
            </div>
          )}

          {error && (
            <div className="state-wrapper">
              <span className="error-icon">⚠️</span>
              <p className="error-text">{error}</p>
            </div>
          )}

          {!loading && !error && formations.length === 0 && (
            <div className="state-wrapper">
              <p>Aucune formation disponible pour le moment.</p>
            </div>
          )}

          {!loading && !error && formations.length > 0 && (
            <div className="formations-grid">
              {formations.map((formation) => {
                const imageUrl = getImageUrl(formation.image);
                return (
                  <div className="formation-card" key={formation.id}>
                    <Link to={`/infos-formations/${formation.titre}/${formation.id}`}>
                    <div className="formation-card-image">
                      {imageUrl ? (
                        <img src={imageUrl} alt={formation.titre} loading="lazy" />
                      ) : (
                        <span className="no-image"><i className="fa fa-image"></i></span>
                      )}
                    </div>
                    </Link>
                    <div className="formation-card-header">
                      <div className="card-title">
                        <h3>{formation.titre}</h3>
                        <span className="card-id">#{formation.id}</span>
                      </div>
                      <span className="card-badge" style={{ backgroundColor: getCategoryColor(formation.categorie) }}>
                        {formation.categorie}
                      </span>
                    </div>
                    <div className="formation-card-body">
                      <p className="card-description">{formation.description}</p>
                      <div className="card-meta">
                        <div className="card-meta-item">
                          <span className="meta-label">Durée / Participants max</span>
                          <span className="meta-value">{formation.duree} / {formation.participantsLimit}</span>
                        </div>
                        <div className="card-meta-item">
                          <span className="meta-label">Prix</span>
                          <span className="meta-value">{formation.prix}USD</span>
                        </div>
                      </div>
                      <div className="card-footer">
                        <button className="card-link" onClick={() => openModal(formation)}>
                          Détails <i className="fa fa-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* MODAL */}
      {selectedFormation && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <i className="fa fa-times"></i>
            </button>
            <div className="modal-header">
              <span className="modal-badge" style={{ backgroundColor: getCategoryColor(selectedFormation.categorie) }}>
                {selectedFormation.categorie}
              </span>
              <h3>{selectedFormation.titre}</h3>
              <span className="modal-id">#{selectedFormation.id}</span>
            </div>
            <div className="modal-body">
              {getImageUrl(selectedFormation.image) && (
                <img
                  src={getImageUrl(selectedFormation.image)}
                  alt={selectedFormation.titre}
                  className="modal-image"
                  loading="lazy"
                />
              )}
              <p className="modal-description">{selectedFormation.description}</p>
              <div className="modal-details">
                <div className="modal-detail-item">
                  <span className="detail-label">Durée</span>
                  <span className="detail-value">{selectedFormation.duree}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="detail-label">Participants max</span>
                  <span className="detail-value">{selectedFormation.participantsLimit}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormationsCards;